<template>
  <div>
    <v-dialog v-model="show" width="600" ref="session-timeout-modal">
      <v-card>
        <v-card-title>
          {{ $t('auto_logoff_warning_title') }}
        </v-card-title>
        <v-card-text v-html="$t('auto_logoff_warning', { time_remaining: timeRemaining })" />
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" @click="keepSessionAlive">{{ $t('keep_me_logged_in') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import AuthUtil from '@/util/AuthUtil'
import DateTimeUtil from '@/util/DateTimeUtil'
import { mapState } from 'vuex'
import Moment from 'moment'

export default {
  name: 'SessionTimeoutModal',
  data () {
    return {
      show: false,
      autoLogoffTimer: null,
      autoLogoffWarningTimer: null,
      secondsRemaining: 0,
      warningSecondsBeforeTimeout: 15 * 60
    }
  },
  computed: {
    /**
     * Make userModule.sessionExpirationTime a computed property on this component.
     * @return Integer
     */
    ...mapState('userModule', {
      sessionExpirationTime: state => state.sessionExpirationTime
    }),

    /**
     * A human readable version of the time remaining in the session.
     * @return String
     */
    timeRemaining () {
      // Countdown as minutes and switch to seconds for the last minute.
      const unit = this.secondsRemaining >= 60 ? 'minutes' : 'seconds'
      // Add 1 minute for display purposes until the last minute. Without this, the second it switches from
      // 3 to 2 minutes, it actually means 2 minutes 59 seconds. This is better represented as 3 minutes.
      // We countdown seconds for the last minute, so switch back to using this.secondsRemaining.
      const seconds = this.secondsRemaining >= 60 ? this.secondsRemaining + 60 : this.secondsRemaining
      return DateTimeUtil.humanizeDurationPrecisely(
        Moment.duration(seconds, 'seconds'),
        { mostPreciseUnit: unit, numberOfSignificantParts: 2, separator: ', ' }
      )
    }
  },
  methods: {
    /**
     * Updates the secondsRemaining property and registers another call to this method if the modal is showing so that
     * the time is continually updated.
     * @return undefined
     */
    updateTimeRemaining () {
      this.secondsRemaining = this.sessionExpirationTime - Moment().unix()
    },
    /**
     * Returns a time in seconds at which the modal should be rendered.
     * @return Integer
     */
    showModalAtTime () {
      const secondsToShowModal = AuthUtil.jwtExpiration(true) - this.warningSecondsBeforeTimeout
      return secondsToShowModal > 0 ? secondsToShowModal : 0
    },
    /**
     * To keep the session alive all we have to do is make an API call. A successful response includes a refreshed JWT,
     * which updates sessionExpirationTime and re-arms the logoff timers via the watcher. Once that call succeeds we
     * close the modal; otherwise it would stay open with a frozen countdown and appear unresponsive.
     * @return undefined
     */
    keepSessionAlive () {
      this.$orm('User').find('self', { params: { fields: 'id' } })
        .then(() => {
          this.show = false
        })
    },
    /**
     * Clear timers/intervals
     * @return undefined
     */
    clearTimers () {
      if (this.autoLogoffTimer !== null) {
        clearTimeout(this.autoLogoffTimer)
      }
      if (this.autoLogoffWarningTimer !== null) {
        clearTimeout(this.autoLogoffWarningTimer)
      }
      if (this.secondsRemainingTimer !== null) {
        clearInterval(this.secondsRemainingTimer)
      }
    }
  },
  watch: {
    /**
     * When the session expiration time changes we need to register two functions, oen to log the user out if they reach
     * the time limit and another to show a warning modal beforehand.
     */
    sessionExpirationTime (newValue, oldValue) {
      // Clear existing timers
      this.clearTimers()
      // Register new functions based on new session expiration tim
      this.autoLogoffTimer = setTimeout(() => {
        AuthUtil.logout()
        this.$router.push('/login')
      }, AuthUtil.jwtExpiration(true) * 1000)
      this.autoLogoffWarningTimer = setTimeout(() => {
        this.updateTimeRemaining()
        this.show = true
        this.secondsRemainingTimer = setInterval(this.updateTimeRemaining, 1000)
      }, this.showModalAtTime() * 1000)
    },
    /**
     * If we get down to 0 then the session has timed out, and we should take the user to the login page.
     */
    secondsRemaining (newValue, oldValue) {
      if (newValue < 1) {
        this.$router.push('/login')
      }
    }
  }
}

</script>
